// Token-driven mock primitives + the "mock kit" (mk) that powers two console
// features: (1) per-element HIGHLIGHT — an element glows when the color row you're
// hovering uses one of its tokens; (2) token USAGE tracking — every color a surface
// touches is recorded so the editor can filter to "only this page's colors".
//
// Every primitive takes `mk` first. Use `mk.c(token)` for a color (records usage)
// and spread `...mk.glow(tokenA, tokenB)` onto an element's style so it lights up
// when any of those tokens is the highlighted one.

const GLOW = {
  boxShadow: "0 0 0 2px #fff, 0 0 9px 3px rgba(90,170,255,0.95)",
  borderRadius: 5,
  position: "relative",
  zIndex: 2,
  transition: "box-shadow 0.1s",
};

// Build a kit around a css resolver (token -> color string) + the highlighted token.
export function makeKit(css, highlightToken) {
  const used = new Set();
  return {
    used,
    highlight: highlightToken || null,
    c(token) {
      used.add(token);
      return css(token);
    },
    glow(...tokens) {
      return highlightToken && tokens.includes(highlightToken) ? GLOW : null;
    },
  };
}

export const grad = (mk, a, b, deg = 135) =>
  `linear-gradient(${deg}deg, ${mk.c(a)}, ${mk.c(b)})`;

// The app's flat-card and strong-divider chrome is NOT tokenized (removed with
// the dead surfaceCardFlat/dividerStrong tokens 2026-07-29): flat cards are a
// static black wash in the app, and dividers derive from textPrimary at low
// alpha. The mocks mirror that honestly — a static wash for flat cards, and a
// textPrimary-derived rgba for dividers (so dividers still follow live edits
// of textPrimary, exactly like the app).
export const FLAT_CARD_BG = "rgba(0,0,0,0.15)";
export const dividerCss = (mk) => {
  const c = mk.c("textPrimary"); // records usage — app dividers derive from it
  const m = /^#([0-9a-fA-F]{6})$/.exec(c);
  if (!m) return c;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},0.2)`;
};

// ── chrome ─────────────────────────────────────────────────────────────────────
export const AppBar = (mk, title, { back = true, actions = [], key } = {}) => (
  <div key={key} style={{ height: 42, display: "flex", alignItems: "center", padding: "0 10px", gap: 8, background: grad(mk, "gradAppBarStart", "gradAppBarEnd"), flexShrink: 0, ...mk.glow("gradAppBarStart", "gradAppBarEnd") }}>
    {back && <span style={{ color: mk.c("textPrimary"), fontSize: 16, ...mk.glow("textPrimary") }}>‹</span>}
    <span style={{ flex: 1, textAlign: "center", color: mk.c("textPrimary"), fontWeight: 700, fontSize: 13, ...mk.glow("textPrimary") }}>{title}</span>
    {actions.map((a, i) => <span key={i} style={{ width: 14, height: 14, borderRadius: 3, background: mk.c("textPrimary"), opacity: 0.85 }} />)}
    {!actions.length && back && <span style={{ width: 8 }} />}
  </div>
);

export const BottomNav = (mk, items = ["Pay", "Calendar", "Jobs", "More"], active = 1) => (
  <div style={{ height: 44, display: "flex", background: grad(mk, "gradBrandRevStart", "gradBrandRevEnd"), flexShrink: 0, boxShadow: `0 -2px 6px ${mk.c("shadow")}`, ...mk.glow("gradBrandRevStart", "gradBrandRevEnd") }}>
    {items.map((it, i) => (
      <div key={it} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3 }}>
        <div style={{ width: 14, height: 14, borderRadius: 4, background: i === active ? mk.c("accentPrimary") : mk.c("textPrimary"), opacity: i === active ? 1 : 0.7, ...(i === active ? mk.glow("accentPrimary") : null) }} />
        <span style={{ fontSize: 7.5, color: mk.c("textPrimary"), opacity: i === active ? 1 : 0.7 }}>{it}</span>
      </div>
    ))}
  </div>
);

export const Fab = (mk, { key } = {}) => (
  <div key={key} style={{ position: "absolute", right: 12, bottom: 12, width: 40, height: 40, borderRadius: "50%", background: grad(mk, "gradBrandStart", "gradBrandEnd"), border: `1.5px solid ${mk.c("textPrimary")}`, display: "flex", alignItems: "center", justifyContent: "center", color: mk.c("textPrimary"), fontSize: 20, boxShadow: `0 3px 8px ${mk.c("shadowStrong")}`, ...mk.glow("gradBrandStart", "gradBrandEnd") }}>+</div>
);

// ── surfaces ─────────────────────────────────────────────────────────────────────
export const Card = (mk, children, { key, gradKey, flat, pad = 10 } = {}) => (
  <div key={key} style={{
    background: gradKey ? grad(mk, gradKey[0], gradKey[1]) : (flat ? FLAT_CARD_BG : mk.c("surfaceCard")),
    border: `1px solid ${mk.c("textPrimary")}`, borderRadius: 10, padding: pad,
    boxShadow: `0 2px 6px ${mk.c("shadowStrong")}`,
    ...mk.glow(gradKey ? gradKey[0] : (flat ? "textPrimary" : "surfaceCard"), gradKey ? gradKey[1] : "textPrimary"),
  }}>{children}</div>
);

export const Btn = (mk, label, { a = "gradBrandStart", b = "gradBrandEnd", icon, key } = {}) => (
  <div key={key} style={{ background: grad(mk, a, b), border: `1px solid ${mk.c("textPrimary")}`, borderRadius: 8, padding: "9px 0", textAlign: "center", color: mk.c("textPrimary"), fontWeight: 600, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, ...mk.glow(a, b) }}>
    {icon && <span style={{ width: 12, height: 12, borderRadius: 3, background: mk.c("textPrimary") }} />}{label}
  </div>
);

// ── form bits ────────────────────────────────────────────────────────────────────
export const Input = (mk, label, { value, focused, key } = {}) => (
  <div key={key} style={{ borderBottom: `2px solid ${focused ? mk.c("inputBorderFocused") : mk.c("inputBorder")}`, padding: "5px 2px", ...mk.glow(focused ? "inputBorderFocused" : "inputBorder") }}>
    <div style={{ color: mk.c("labelText"), fontSize: 8, opacity: 0.75, ...mk.glow("labelText") }}>{label}</div>
    <div style={{ color: value ? mk.c("textPrimary") : mk.c("hintText"), fontSize: 11, marginTop: 1, ...mk.glow(value ? "textPrimary" : "hintText") }}>{value || "…"}</div>
  </div>
);

export const Chip = (mk, tok, label, { key } = {}) => (
  <span key={key} style={{ background: mk.c(tok), color: mk.c("textOnPrimary"), fontSize: 9, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap", ...mk.glow(tok) }}>{label}</span>
);

export const Toggle = (mk, label, on = true, { key } = {}) => (
  <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <span style={{ color: mk.c("textPrimary"), fontSize: 11, flex: 1, ...mk.glow("textPrimary") }}>{label}</span>
    <span style={{ width: 32, height: 17, borderRadius: 9, background: on ? mk.c("switchOnTrack") : mk.c("switchOffTrack"), position: "relative", display: "inline-block", ...mk.glow(on ? "switchOnTrack" : "switchOffTrack") }}>
      <span style={{ position: "absolute", top: 2, [on ? "right" : "left"]: 2, width: 13, height: 13, borderRadius: "50%", background: on ? mk.c("switchOnThumb") : mk.c("switchOffThumb") }} />
    </span>
  </div>
);

export const Radio = (mk, label, selected, { key } = {}) => (
  <div key={key} style={{ display: "flex", alignItems: "center", gap: 7 }}>
    <span style={{ width: 13, height: 13, borderRadius: "50%", border: `2px solid ${selected ? mk.c("radioActive") : mk.c("radioOff")}`, display: "inline-flex", alignItems: "center", justifyContent: "center", ...mk.glow(selected ? "radioActive" : "radioOff") }}>
      {selected && <span style={{ width: 6, height: 6, borderRadius: "50%", background: mk.c("radioActive") }} />}
    </span>
    <span style={{ color: mk.c("textPrimary"), fontSize: 11 }}>{label}</span>
  </div>
);

export const PhotoTile = (mk, { filled, key } = {}) => (
  <div key={key} style={{ width: 46, height: 46, borderRadius: 8, background: filled ? grad(mk, "gradBrandStart", "gradBrandEnd") : mk.c("surfaceListItem"), border: filled ? "none" : `1px dashed ${mk.c("textMuted")}`, display: "flex", alignItems: "center", justifyContent: "center", color: mk.c("textMuted"), fontSize: 16, ...(filled ? null : mk.glow("surfaceListItem")) }}>{filled ? "" : "+"}</div>
);

// ── list / data ──────────────────────────────────────────────────────────────────
export const ListRow = (mk, title, sub, chip, { avatar = true, key } = {}) => (
  <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", background: mk.c("surfaceListItem"), borderRadius: 8, ...mk.glow("surfaceListItem") }}>
    {avatar && <div style={{ width: 22, height: 22, borderRadius: "50%", background: mk.c("accentPrimary"), flexShrink: 0, ...mk.glow("accentPrimary") }} />}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ color: mk.c("textPrimary"), fontSize: 11, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...mk.glow("textPrimary") }}>{title}</div>
      {sub && <div style={{ color: mk.c("textMuted"), fontSize: 9, ...mk.glow("textMuted") }}>{sub}</div>}
    </div>
    {chip && Chip(mk, chip[0], chip[1])}
  </div>
);

export const MoneyRow = (mk, label, amt, { tok = "success", strong, key } = {}) => (
  <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${dividerCss(mk)}` }}>
    <span style={{ color: mk.c("textPrimary"), fontSize: 11, fontWeight: strong ? 700 : 400, ...mk.glow("textPrimary") }}>{label}</span>
    <span style={{ color: mk.c(tok), fontSize: strong ? 13 : 12, fontWeight: 700, ...mk.glow(tok) }}>{amt}</span>
  </div>
);

export const Tabs = (mk, tabs, active = 0) => (
  <div style={{ display: "flex", gap: 12, borderBottom: `1px solid ${dividerCss(mk)}`, padding: "0 2px" }}>
    {tabs.map((t, i) => (
      <span key={t} style={{ fontSize: 11, padding: "6px 0", color: i === active ? mk.c("accentPrimary") : mk.c("textMuted"), borderBottom: `2px solid ${i === active ? mk.c("accentPrimary") : "transparent"}`, fontWeight: i === active ? 700 : 400, ...(i === active ? mk.glow("accentPrimary") : null) }}>{t}</span>
    ))}
  </div>
);

export const Pills = (mk, items, active = 0) => (
  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
    {items.map((it, i) => (
      <span key={it} style={{ fontSize: 10, padding: "3px 9px", borderRadius: 12, border: `1px solid ${i === active ? mk.c("accentPrimary") : mk.c("textMuted")}`, color: i === active ? mk.c("accentPrimary") : mk.c("textMuted"), background: i === active ? undefined : "transparent", ...(i === active ? mk.glow("accentPrimary") : null) }}>{it}</span>
    ))}
  </div>
);

export const SectionLabel = (mk, text, { key } = {}) => (
  <div key={key} style={{ color: mk.c("textMuted"), fontSize: 8.5, textTransform: "uppercase", letterSpacing: "0.06em", margin: "3px 0 2px", ...mk.glow("textMuted") }}>{text}</div>
);

export const Text = (mk, children, { tok = "textPrimary", size = 11, weight, key } = {}) => (
  <div key={key} style={{ color: mk.c(tok), fontSize: size, fontWeight: weight, ...mk.glow(tok) }}>{children}</div>
);

// ── chart primitives ───────────────────────────────────────────────────────────
// Added 2026-08-07 for the business-metrics surface. None of the primitives above
// draws a bar, line, axis or arc, so without these the ~19 new metric* tokens
// would be unreadable in the console — and an unread token is unverifiable (the
// rule that shipped a white-on-white drawer in build 274).

/** Bar chart with an optional trailing PROJECTED bar (hatched, never solid). */
export const MockBarChart = (mk, { values = [3, 5, 4, 7, 6, 9], projected = 4, key } = {}) => {
  const max = Math.max(...values, projected, 1);
  return (
    <div key={key} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 46, borderBottom: `1px solid ${mk.c("metricAxisLine")}`, ...mk.glow("metricAxisLine") }}>
        {values.map((v, i) => (
          <div key={i} style={{ flex: 1, height: `${(v / max) * 100}%`, borderRadius: "3px 3px 0 0", background: mk.c("metricRevenueBar"), ...mk.glow("metricRevenueBar") }} />
        ))}
        {projected > 0 && (
          <div style={{ flex: 1, height: `${(projected / max) * 100}%`, borderRadius: "3px 3px 0 0", background: mk.c("metricProjectedBar"), border: `1px dashed ${mk.c("metricProjectedBadge")}`, ...mk.glow("metricProjectedBar", "metricProjectedBadge") }} />
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 6.5, color: mk.c("metricAxisLabel"), ...mk.glow("metricAxisLabel") }}>2014</span>
        <span style={{ fontSize: 6.5, color: mk.c("metricAxisLabel") }}>2026</span>
      </div>
    </div>
  );
};

/** Seasonality-style line with a mean gridline. */
export const MockLineChart = (mk, { points = [4, 3, 6, 7, 5, 8, 9, 7, 6, 9, 7, 4], key } = {}) => {
  const max = Math.max(...points, 1);
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${(i / (points.length - 1)) * 100} ${34 - (p / max) * 30}`)
    .join(" ");
  return (
    <div key={key} style={{ position: "relative", height: 40 }}>
      <div style={{ position: "absolute", left: 0, right: 0, top: 19, borderTop: `1px dashed ${mk.c("metricGridLine")}`, ...mk.glow("metricGridLine") }} />
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" style={{ width: "100%", height: 40, position: "relative" }}>
        <path d={d} fill="none" stroke={mk.c("metricRevenueBar")} strokeWidth="2" vectorEffect="non-scaling-stroke" />
      </svg>
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", ...mk.glow("metricRevenueBar") }} />
    </div>
  );
};

/** Job-type mix donut + legend. Identity is never carried by colour alone. */
export const MockDonut = (mk, { key } = {}) => {
  const segs = [
    ["metricSeriesMaint", "Maint", 46],
    ["metricSeriesRefinish", "Refinish", 48],
    ["metricSeriesOther", "Other", 6],
  ];
  let acc = 0;
  const stops = segs
    .map(([tok, , pctv]) => {
      const from = acc;
      acc += pctv;
      return `${mk.c(tok)} ${from}% ${acc}%`;
    })
    .join(", ");
  return (
    <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 42, height: 42, borderRadius: "50%", background: `conic-gradient(${stops})`, position: "relative", ...mk.glow("metricSeriesMaint", "metricSeriesRefinish", "metricSeriesOther") }}>
        <div style={{ position: "absolute", inset: 11, borderRadius: "50%", background: FLAT_CARD_BG }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {segs.map(([tok, label, pctv]) => (
          <span key={label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 7, color: mk.c("metricTileLabel") }}>
            <span style={{ width: 6, height: 6, borderRadius: 2, background: mk.c(tok), ...mk.glow(tok) }} />
            {label} {pctv}%
          </span>
        ))}
      </div>
    </div>
  );
};

/** KPI tile row: label, value, and an up/down/neutral delta. */
export const MockStatTile = (mk, { key } = {}) => {
  const tiles = [
    ["TTM", "$552k", "metricDeltaPositive", "▲ 22.1%"],
    ["Avg Job", "$580", "metricDeltaNegative", "▼ 3.4%"],
    ["Jobs", "587", "metricDeltaNeutral", "--"],
  ];
  return (
    <div key={key} style={{ display: "flex", gap: 4 }}>
      {tiles.map(([label, value, deltaTok, delta]) => (
        <div key={label} style={{ flex: 1, background: FLAT_CARD_BG, borderRadius: 5, padding: "4px 5px" }}>
          <div style={{ fontSize: 6.5, textTransform: "uppercase", letterSpacing: "0.05em", color: mk.c("metricTileLabel"), ...mk.glow("metricTileLabel") }}>{label}</div>
          <div style={{ fontSize: 11, fontWeight: 800, color: mk.c("metricTileValue"), ...mk.glow("metricTileValue") }}>{value}</div>
          <div style={{ fontSize: 7, fontWeight: 700, color: mk.c(deltaTok), ...mk.glow(deltaTok) }}>{delta}</div>
        </div>
      ))}
    </div>
  );
};

/**
 * Ranked rows, one per categorical slot. The token names are spelled out rather
 * than built with `metricCat${i}` on purpose: a computed token name is invisible
 * to a reader and to the "is every token consumed by a mock?" check, so the
 * colour would be unverifiable even though it renders.
 *
 * Slot order is the VALIDATED adjacent order (peacock, tangerine, grape, banana,
 * flamingo) — the app's source order puts the two red-oranges side by side, which
 * fails the normal-vision separation floor.
 */
const CAT_SLOTS = ["metricCat1", "metricCat2", "metricCat3", "metricCat4", "metricCat5"];

export const MockRankedBars = (mk, { rows = [["Kyle", 100], ["Goose", 72], ["Seamus", 54], ["Biggin", 38], ["Chris", 22]], key } = {}) => (
  <div key={key} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
    {rows.map(([label, w], i) => {
      const tok = CAT_SLOTS[i % CAT_SLOTS.length];
      return (
        <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 34, fontSize: 7, color: mk.c("metricTileLabel") }}>{label}</span>
          <span style={{ flex: 1, height: 6, borderRadius: 3, background: mk.c(tok), width: `${w}%`, ...mk.glow(tok) }} />
        </div>
      );
    })}
  </div>
);

// The dark app background most BDR screens sit on (screenBg is transparent, so brand blue shows).
export const bg = (mk) => mk.c("gradBrandStart");
